import {
  Box,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { endpoints } from "../configs/config";
import dayjs from "dayjs";

let cachedFrontendVersion = null;
let cachedBackendVersion = null;
let cachedDatabaseVersion = null;

const normalizeVersionPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return {
      version: payload ?? null,
      buildDate: null,
      gitHash: null,
    };
  }

  return {
    version: payload.version ?? payload.Version ?? null,
    buildDate: payload.buildDate ?? payload.BuildDate ?? null,
    gitHash: payload.gitHash ?? payload.GitHash ?? null,
  };
};

const fetchFrontendVersionOnce = async ({ signal } = {}) => {
  if (cachedFrontendVersion) return cachedFrontendVersion;
  const url = `${process.env.PUBLIC_URL || ""}/version.json`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  const data = await res.json();
  cachedFrontendVersion = normalizeVersionPayload(data);
  return cachedFrontendVersion;
};

const fetchBackendVersionOnce = async ({ signal } = {}) => {
  if (cachedBackendVersion) return cachedBackendVersion;
  const res = await fetch(endpoints.backendVersionUrl, { signal });
  if (!res.ok)
    throw new Error(
      `Failed to fetch backend version: ${res.status} ${res.statusText}`
    );
  const data = await res.json();
  cachedBackendVersion = normalizeVersionPayload(data);
  return cachedBackendVersion;
};

const fetchDatabaseVersionOnce = async ({ signal } = {}) => {
  if (cachedDatabaseVersion) return cachedDatabaseVersion;
  const res = await fetch(endpoints.databaseVersionUrl, { signal });
  if (!res.ok)
    throw new Error(
      `Failed to fetch database version: ${res.status} ${res.statusText}`
    );
  const data = await res.text();
  cachedDatabaseVersion = data;
  return cachedDatabaseVersion;
};

const formatShortHash = (hash) =>
  hash ? String(hash).substring(0, 8) : "unbekannt";

const formatDateTime = (date) => {
  if (!date) return "unbekannt";
  const formatted = dayjs(date).format("DD.MM.YYYY HH:mm:ss");
  return formatted === "Invalid Date" ? "unbekannt" : formatted;
};

const AppBarSystemInfoModal = ({ openModal, handleClose }) => {
  const [backendVersion, setBackendVersion] = useState(null);
  const [frontendVersion, setFrontendVersion] = useState(null);
  const [databaseVersion, setDatabaseVersion] = useState(null);
  const [isFetchingFrontend, setIsFetchingFrontend] = useState(false);
  const [isFetchingBackend, setIsFetchingBackend] = useState(false);
  const [isFetchingDatabase, setIsFetchingDatabase] = useState(false);

  useEffect(() => {
    if (!openModal) return;

    const controller = new AbortController();
    let cancelled = false;

    setIsFetchingFrontend(true);
    setIsFetchingBackend(true);
    setIsFetchingDatabase(true);

    (async () => {
      try {
        const [frontendResult, backendResult, databaseResult] =
          await Promise.allSettled([
            fetchFrontendVersionOnce({ signal: controller.signal }),
            fetchBackendVersionOnce({ signal: controller.signal }),
            fetchDatabaseVersionOnce({ signal: controller.signal }),
          ]);

        if (cancelled) return;

        if (frontendResult.status === "fulfilled") {
          setFrontendVersion(frontendResult.value);
        } else {
          setFrontendVersion((prev) => prev ?? "Fehler");
        }

        if (backendResult.status === "fulfilled") {
          setBackendVersion(backendResult.value);
        } else {
          setBackendVersion((prev) => prev ?? "Fehler");
        }

        if (databaseResult.status === "fulfilled") {
          setDatabaseVersion(databaseResult.value);
        } else {
          setDatabaseVersion((prev) => prev ?? "Fehler");
        }
      } catch (e) {
        if (cancelled) return;
        setBackendVersion((prev) => prev ?? "Fehler");
        setFrontendVersion((prev) => prev ?? "Fehler");
        setDatabaseVersion((prev) => prev ?? "Fehler");
      } finally {
        if (cancelled) return;
        setIsFetchingFrontend(false);
        setIsFetchingBackend(false);
        setIsFetchingDatabase(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [openModal]);

  return (
    <Modal open={openModal} onClose={handleClose}>
      <Box sx={modalContentstyle}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h6" fontWeight={"bold"} mb={2}>
            Systeminfo
          </Typography>
          <Stack sx={{ flexDirection: "row", gap: 1 }}>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              data-cy="appbar-systeminfo-schließen"
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Typography variant="h6">Versionen</Typography>
        <DividerStyled />

        <Typography variant="body1" data-cy="frontend-version">
          Frontend:{" "}
          {isFetchingFrontend ? (
            "Lädt..."
          ) : (
            <b>{frontendVersion?.version ?? frontendVersion ?? "unbekannt"}</b>
          )}
        </Typography>

        <Typography variant="caption" component="p">
          {isFetchingFrontend
            ? "Lädt..."
            : formatDateTime(frontendVersion?.buildDate)}
        </Typography>

        <Typography variant="caption" component="p">
          {isFetchingFrontend
            ? "Lädt..."
            : formatShortHash(frontendVersion?.gitHash)}
        </Typography>

        <DividerStyled />

        <Typography data-cy="backend-version">
          Backend:{" "}
          {isFetchingBackend ? (
            "Lädt..."
          ) : (
            <b>{backendVersion?.version ?? backendVersion ?? "unbekannt"}</b>
          )}
        </Typography>

        <Typography variant="caption" component="p">
          {isFetchingBackend
            ? "Lädt..."
            : formatDateTime(backendVersion?.buildDate)}
        </Typography>

        <Typography variant="caption" component="p">
          {isFetchingBackend
            ? "Lädt..."
            : formatShortHash(backendVersion?.gitHash)}
        </Typography>

        <DividerStyled />
        <Typography data-cy="datenbank-version">
          Datenbank:{" "}
          {isFetchingDatabase ? (
            "Lädt..."
          ) : (
            <b>{databaseVersion ?? "unbekannt"}</b>
          )}
        </Typography>
      </Box>
    </Modal>
  );
};

export default AppBarSystemInfoModal;

const DividerStyled = () => {
  return <Divider style={{ marginTop: 3, marginBottom: 3 }} />;
};

const modalContentstyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  minWidth: "300px",
};
